import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { api, formatApiErrorDetail } from "../lib/api";
import { Download, Loader2, FileText } from "lucide-react";
import LogoMark from "../components/LogoMark";

const ALLOWED_TAGS = ["h2", "h3", "p", "ul", "li", "strong", "em", "table", "thead", "tbody", "tr", "th", "td", "blockquote", "br"];
const SANITIZE_CONFIG = {
  ALLOWED_TAGS,
  ALLOWED_ATTR: [], // no attributes at all — the system prompt forbids them
  KEEP_CONTENT: true,
};

export default function FundingReport() {
  const [params] = useSearchParams();
  const id = params.get("id");
  const email = params.get("email");
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id || !email) {
      setError("Missing report link parameters. Please use the link from your confirmation email.");
      setLoading(false);
      return;
    }
    api.get(`/funding/report/${id}`, { params: { email } })
      .then((r) => setReport(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Report not available."))
      .finally(() => setLoading(false));
  }, [id, email]);

  // Sanitize the AI-generated HTML — defense-in-depth against any prompt-injection
  // that could try to slip in <script>, event handlers, or external resources.
  const safeHtml = useMemo(
    () => (report?.report_html ? DOMPurify.sanitize(report.report_html, SANITIZE_CONFIG) : ""),
    [report?.report_html]
  );

  const print = () => window.print();

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center pt-24" data-testid="report-loading">
        <Loader2 size={28} style={{ color: "var(--brass)" }} className="animate-spin" />
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="max-w-2xl mx-auto pt-32 pb-24 px-6 text-center" data-testid="report-error">
        <p className="eyebrow mb-4">Report Unavailable</p>
        <h1 className="font-display text-5xl mb-6">{error || "Report not found."}</h1>
        <p style={{ color: "var(--ink-muted)" }}>
          Reports typically take 5-10 minutes to generate after submission. If you submitted recently,
          please check back shortly. Otherwise, contact the editorial desk.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="report-page" className="max-w-[900px] mx-auto px-6 lg:px-10 pt-32 pb-24">
      {/* Header — only visible on screen, hidden on print */}
      <div className="mb-12 print:hidden">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-6">
          <div>
            <p className="eyebrow mb-3">Wealth First Inc. · AI Funding Compliance Report</p>
            <h1 className="font-display text-5xl md:text-6xl leading-[0.95]">
              Compliance & <em style={{ color: "var(--brass)" }}>Regulatory Readiness</em>.
            </h1>
          </div>
          <button onClick={print} className="btn-brass shrink-0" data-testid="report-download">
            <Download size={14} /> Save as PDF
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 py-6" style={{ borderTop: "1px solid var(--hairline)", borderBottom: "1px solid var(--hairline)" }}>
          <div>
            <p className="eyebrow mb-1">Prepared For</p>
            <p className="text-sm">{report.name}</p>
          </div>
          <div>
            <p className="eyebrow mb-1">Business</p>
            <p className="text-sm">{report.business_name || "—"}</p>
          </div>
          <div>
            <p className="eyebrow mb-1">Plan</p>
            <p className="text-sm">{report.plan?.replace(/_/g, " ")}</p>
          </div>
          <div>
            <p className="eyebrow mb-1">Generated</p>
            <p className="text-sm">{report.report_generated_at ? new Date(report.report_generated_at).toLocaleDateString() : "—"}</p>
          </div>
        </div>
      </div>

      {/* Print-only header */}
      <div className="hidden print:block mb-8">
        <div className="flex items-center gap-4 mb-4">
          <LogoMark height={48} width={48} color="#000" />
          <div>
            <p style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "#666" }}>Wealth First Inc.</p>
            <h1 style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 32, margin: 0 }}>AI Funding Compliance Report</h1>
          </div>
        </div>
        <p style={{ fontSize: 12, color: "#444" }}>Prepared for <strong>{report.name}</strong> · {report.business_name || "—"} · Generated {report.report_generated_at ? new Date(report.report_generated_at).toLocaleDateString() : "—"}</p>
      </div>

      <article
        data-testid="report-content"
        className="report-content"
        dangerouslySetInnerHTML={{ __html: safeHtml }}
      />

      <div className="mt-16 pt-10 print:hidden text-center" style={{ borderTop: "1px solid var(--hairline)" }}>
        <FileText size={20} style={{ color: "var(--brass)", margin: "0 auto 1rem" }} />
        <p className="text-xs uppercase tracking-[0.22em]" style={{ color: "var(--ink-faint)" }}>
          End of Report · Wealth First Inc. Funding Compliance Desk
        </p>
      </div>

      <style>{`
        .report-content h2 {
          font-family: 'Cormorant Garamond', serif;
          font-size: 2.5rem;
          color: var(--brass);
          margin: 3rem 0 1.5rem 0;
          padding-top: 2rem;
          border-top: 1px solid var(--hairline);
          letter-spacing: -0.02em;
        }
        .report-content h2:first-child { border-top: none; padding-top: 0; margin-top: 0; }
        .report-content h3 {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.75rem;
          color: var(--ink);
          margin: 2.5rem 0 1rem 0;
        }
        .report-content p {
          font-size: 1.0625rem;
          line-height: 1.85;
          color: var(--ink);
          margin: 1rem 0;
        }
        .report-content ul { margin: 1rem 0 1.5rem 0; padding-left: 0; list-style: none; }
        .report-content li {
          position: relative; padding-left: 1.75rem; margin: 0.6rem 0;
          color: var(--ink); line-height: 1.7; font-size: 1rem;
        }
        .report-content li::before {
          content: '✦'; color: var(--brass); position: absolute; left: 0; top: 0;
        }
        .report-content strong { color: var(--brass); font-weight: 600; }
        .report-content em { font-style: italic; color: var(--ink-muted); }
        .report-content blockquote {
          border-left: 2px solid var(--brass); padding: 0.5rem 0 0.5rem 1.5rem;
          margin: 1.5rem 0; font-family: 'Cormorant Garamond', serif;
          font-style: italic; font-size: 1.25rem; color: var(--ink-muted);
        }
        .report-content table {
          width: 100%; border-collapse: collapse; margin: 1.5rem 0;
          border: 1px solid var(--hairline);
        }
        .report-content th {
          background: var(--surface); padding: 0.875rem 1rem;
          text-align: left; font-size: 0.75rem;
          font-family: 'Manrope', sans-serif; font-weight: 600;
          letter-spacing: 0.18em; text-transform: uppercase; color: var(--brass);
          border-bottom: 1px solid var(--hairline-strong);
        }
        .report-content td {
          padding: 0.875rem 1rem; border-top: 1px solid var(--hairline);
          font-size: 0.95rem; color: var(--ink); line-height: 1.6;
        }
        @media print {
          body { background: white !important; color: black !important; }
          .report-content h2 { color: #6b5235; border-top-color: #ccc; }
          .report-content strong { color: #6b5235; }
          .report-content p, .report-content li, .report-content td { color: #111; }
          .report-content table, .report-content th, .report-content td { border-color: #ddd; }
          .report-content th { background: #f5f0e6; color: #6b5235; }
          .report-content li::before { color: #6b5235; }
        }
      `}</style>
    </div>
  );
}
